/**
 * Merges content stack data and Alpaca data into a single text output.
 *
 * @param {Object} contentStackData - The data from the content stack to be processed.
 * @param {Object} alpacaData - The data from Alpaca to be merged with the content stack data.
 * @param {Array<string>} orderedPurposeIdsList - An ordered list of purpose IDs to guide the merging process.
 * @returns {string} The merged text combining content stack and Alpaca data.
 */
export const handleMergeContentStackAndAlpacaData = ( contentStackData, alpacaData, orderedPurposeIdsList ) => {
    const splittedContentStackData = splitContentStackDataByToken(contentStackData);
    const alpacaText = getAlpacaDataText(alpacaData, orderedPurposeIdsList);
    const mergedText = mergeContentStackAndAlpacaData(splittedContentStackData, alpacaText);
    return mergedText
}

/**
 * Merges two arrays of strings by combining corresponding elements from each array.
 * If an element in the second array (`alpacaData`) exists at the same index as an element
 * in the first array (`splittedContentStackData`), the two elements are concatenated with a space.
 * If no corresponding element exists in `alpacaData`, the element from `splittedContentStackData` is used as is.
 * 
 * @param {string[]} splittedContentStackData - An array of strings representing the first set of data.
 * @param {string[]} alpacaData - An array of strings representing the second set of data.
 * @returns {string} A single string resulting from merging and joining the two arrays.
 */
export const mergeContentStackAndAlpacaData = ( splittedContentStackData, alpacaData ) => {
    const mergedData = splittedContentStackData.map((item, index) => {
        if (alpacaData[index]) {
            return item.trim() + ' ' + alpacaData[index];
        }
        return item;
    });
    return mergedData.join(' ');
}

/**
 * Splits the given content stack data string by a specified token and trims each resulting segment.
 *
 * @param {string} contentStackData - The content stack data to be split.
 * @param {string} [token='<br />'] - The token used to split the content stack data. Defaults to '<br />'.
 * @returns {string[]} An array of trimmed strings obtained by splitting the input data by the token.
 */
export const splitContentStackDataByToken = ( contentStackData, token='<br />' ) => {
    const splittedContentStackData = contentStackData.split(token);
    return splittedContentStackData.map((item) => {
        return item.trim();
    });
}

/**
 * Generates an array of text values (verbiage) from alpacaData based on the order of purpose IDs.
 *
 * @param {Object} alpacaData - An object containing alpaca data, where each key maps to an object with a `purposeId` and `verbiageValue`.
 * @param {Array<string>} orderedPurposeIdsList - An array of purpose IDs in the desired order.
 * @returns {Array<string>} An array of text values (`verbiageValue`) corresponding to the ordered purpose IDs.
 */
export const getAlpacaDataText = ( alpacaData, orderedPurposeIdsList ) => {
    const alpacaDataValues = Object.values(alpacaData);
    const alpacaText = [];
    orderedPurposeIdsList.forEach((purposeId) => {
        const alpacaDataFound = alpacaDataValues.find((item) => {
            return item.purposeId === purposeId 
        })
        if (alpacaDataFound) {
            alpacaText.push(alpacaDataFound.verbiageValue);
        }
    });
    return alpacaText
}
